import { View, CardContent, Card, Button } from "@/components/ui";
import { WhatsappSupportBannerBall } from "@/components/shared/whatsapp-support-banner";
import { generateBookingChatSalesMessage, onChatSales } from "@/lib/chat-sales";
import { useEffect, useState } from "react";
import { AnimatedCaret } from "@/components/shared/animated-caret";
import { OrderDetails } from "./order-details";
import { useBookingOrder } from "@/components/hooks/use-booking-order";
import { formatPrice } from "@/lib/helpers/number";
import { cn } from "@/lib/utils";
import { IfElse } from "@/components/shared/if-else";
import { Typography } from "../../typography";
import type { IconNames } from "../../icons/icon-map";
import type { ServiceName } from "@/types/service";

export const BookingNavigationBottom = ({
  name,
  buttonText = "Continue",
  loading,
  disabled,
  onContinue,
  showExtendIcon = true,
  icon = "arrowRight",
  iconAlignment = "end",
}: {
  name: ServiceName;
  loading?: boolean;
  buttonText?: string;
  showExtendIcon?: boolean;
  disabled?: boolean;
  onContinue?: () => void;
  icon?: IconNames;
  iconAlignment?: "start" | "end";
}) => {
  const [showDetail, setShowDetail] = useState(false);
  const toggleDetail = () => {
    setShowDetail((prevState) => !prevState);
  };

  const { grandTotal, lineItems } = useBookingOrder();

  useEffect(() => {
    if (!showExtendIcon) setShowDetail(false);
  }, [showExtendIcon]);

  return (
    <Card
      className="fixed bottom-0 left-0 z-20 block w-full gap-4 md:hidden"
      shadow="sm"
      radius="md"
    >
      <WhatsappSupportBannerBall
        onClick={() => onChatSales(true, generateBookingChatSalesMessage(name))}
      />
      <CardContent className="p-4">
        <View onClick={toggleDetail}>
          {showDetail && <OrderDetails />}
          <IfElse if={lineItems.length > 0}>
            <View className="flex flex-row items-center justify-between py-1">
              <Typography variant="label-sm">
                {lineItems?.length > 1
                  ? `Total Items ${lineItems.length || 0}`
                  : `Total Item ${lineItems.length || 0}`}
              </Typography>
              <View className="flex cursor-pointer flex-row items-center gap-1">
                <Typography color="brand-primary" variant="label-sm">
                  {formatPrice(grandTotal)}
                </Typography>
                <Typography
                  variant="caption-md"
                  color="foreground-intermediate"
                >
                  w/GST
                </Typography>
                <View className={cn({ hidden: !showExtendIcon })}>
                  <AnimatedCaret isExpanded={!showDetail} />
                </View>
              </View>
            </View>
          </IfElse>
        </View>
        <View className="flex flex-col py-0 md:py-4">
          <Button
            variant="primary"
            color="CTA"
            loading={loading}
            iconAlignment={iconAlignment}
            iconName={icon}
            onClick={onContinue}
            disabled={disabled}
            children={buttonText}
            className="w-full"
          />
          <Typography
            variant="caption-md"
            color="brand-primary"
            className="mt-2"
            align={"center"}
          >
            No payment required now. Invoice issued after service completion
          </Typography>
        </View>
      </CardContent>
    </Card>
  );
};
